import { Trans, useTranslation } from "./i18n";
import { DRIVE_SYNC_RELEASED } from "./lib/features";
import "./ipad-guide.css";

const appIcon = "/icons/kexu-v3-192.png";

function ShareIcon() {
  return (
    <svg className="site-ipad-share-icon" viewBox="0 0 24 27" aria-hidden="true">
      <path d="M7 10H4v14h16V10h-3M12 17V2M7 7l5-5 5 5" />
    </svg>
  );
}

function BrowserScene({ share = false }: { share?: boolean }) {
  const { t } = useTranslation("ipadGuide");
  return (
    <div className="site-ipad-browser">
      <div className="site-ipad-toolbar">
        <span className="site-ipad-chevron">‹</span>
        <span className="site-ipad-address">{t("websiteLabel")}</span>
        {share && (
          <span className="site-ipad-share site-ipad-focus">
            <ShareIcon />
          </span>
        )}
      </div>
      <div className="site-ipad-browser-content">
        <div className="site-ipad-mini-brand">
          <img src={appIcon} width={32} height={32} alt="" />
          <b>{t("brandName")}</b>
        </div>
        <div className="site-ipad-placeholder-title" />
        <div className="site-ipad-placeholder-line" />
        <div className="site-ipad-placeholder-line site-ipad-placeholder-short" />
      </div>
    </div>
  );
}

function ShareScene() {
  const { t } = useTranslation("ipadGuide");
  return (
    <>
      <BrowserScene share />
      <span className="site-ipad-callout site-ipad-share-callout">{t("shareLabel")}</span>
    </>
  );
}

function ChooseScene() {
  const { t } = useTranslation("ipadGuide");
  return (
    <>
      <BrowserScene />
      <div className="site-ipad-dim" />
      <div className="site-ipad-sheet">
        <div className="site-ipad-sheet-source">
          <img src={appIcon} width={38} height={38} alt="" />
          <span>
            <b>{t("brandName")}</b>
            <small>{t("shareMenuLabel")}</small>
          </span>
        </div>
        <div className="site-ipad-sheet-row site-ipad-sheet-first">
          {t("copyLabel")}
          <span>⧉</span>
        </div>
        <div className="site-ipad-sheet-row">
          {t("bookmarkLabel")}
          <span>＋</span>
        </div>
        <div className="site-ipad-sheet-row site-ipad-sheet-current">
          {t("addToHomeLabel")}
          <span className="site-ipad-plus">＋</span>
        </div>
      </div>
    </>
  );
}

function ConfirmScene() {
  const { t } = useTranslation("ipadGuide");
  return (
    <>
      <div className="site-ipad-dim" />
      <div className="site-ipad-sheet site-ipad-confirm-sheet">
        <div className="site-ipad-sheet-header">
          <span>{t("cancelLabel")}</span>
          <b>{t("addToHomeLabel")}</b>
          <span className="site-ipad-add site-ipad-focus">{t("addLabel")}</span>
        </div>
        <div className="site-ipad-name">
          <img src={appIcon} width={50} height={50} alt="" />
          <span>{t("brandName")}</span>
        </div>
        <div className="site-ipad-toggle-row">
          <span>{t("openAsAppLabel")}</span>
          <span className="site-ipad-toggle" />
        </div>
        <p className="site-ipad-confirm-hint">{t("optionalToggleHint")}</p>
      </div>
    </>
  );
}

function OpenScene() {
  const { t } = useTranslation("ipadGuide");
  return (
    <>
      <div className="site-ipad-home-top">
        <span>{t("homeScreenLabel")}</span>
        <span>{t("illustrationLabel")}</span>
      </div>
      <div className="site-ipad-apps">
        <div className="site-ipad-app">
          <div className="site-ipad-other-app">
            <span className="site-ipad-notes-lines" />
          </div>
          <span>{t("notesAppLabel")}</span>
        </div>
        <div className="site-ipad-app">
          <img className="site-ipad-focus-app" src={appIcon} width={66} height={66} alt="" />
          <b>{t("brandName")}</b>
        </div>
        <div className="site-ipad-app">
          <div className="site-ipad-other-app">
            <span className="site-ipad-compass" />
          </div>
          <span>{t("safariLabel")}</span>
        </div>
      </div>
      <span className="site-ipad-callout site-ipad-home-callout">{t("alwaysOpenHere")}</span>
      <div className="site-ipad-home-indicator" />
    </>
  );
}

const steps = [
  { title: "stepShareTitle", description: "stepShareDescription", alt: "stepShareAlt", Scene: ShareScene },
  { title: "stepChooseTitle", description: "stepChooseDescription", alt: "stepChooseAlt", Scene: ChooseScene },
  { title: "stepConfirmTitle", description: "stepConfirmDescription", alt: "stepConfirmAlt", Scene: ConfirmScene },
  { title: "stepOpenTitle", description: "stepOpenDescription", alt: "stepOpenAlt", Scene: OpenScene, home: true },
] as const;

export function IpadGuideSpotlight() {
  const { t } = useTranslation("ipadGuide");
  return (
    <section className="site-section site-ipad-spotlight" aria-labelledby="ipad-spotlight-title">
      <img src={appIcon} width={68} height={68} alt="" />
      <div>
        <p className="site-eyebrow">{t("spotlightKicker")}</p>
        <h2 id="ipad-spotlight-title">{t("spotlightTitle")}</h2>
        <p>{t("spotlightDescription")}</p>
        <a className="site-text-link" href="/ipad">
          {t("spotlightLink")} <span aria-hidden="true">↗</span>
        </a>
      </div>
    </section>
  );
}

export default function IpadGuide() {
  const { t } = useTranslation("ipadGuide");
  return (
    <div className="site-ipad-guide">
      <section className="site-page-hero site-section">
        <p className="site-eyebrow">{t("kicker")}</p>
        <h1 id="landing-title" tabIndex={-1}>
          <Trans t={t} i18nKey="title" />
        </h1>
        <p className="site-lead">{t("lead")}</p>
        <p className="site-ipad-first-use">
          <Trans t={t} i18nKey="firstUse" />
        </p>
      </section>
      <aside className="site-section site-ipad-reminder" aria-labelledby="ipad-entry-title">
        <img src={appIcon} width={46} height={46} alt="" />
        <div>
          <h2 id="ipad-entry-title">{t("reminderTitle")}</h2>
          <p>
            <Trans t={t} i18nKey="reminder" />
          </p>
          <p className="site-ipad-reminder-extra">{t("reminderExtra")}</p>
        </div>
      </aside>
      <section className="site-section site-ipad-steps-section" aria-labelledby="ipad-steps-title">
        <div className="site-ipad-steps-header">
          <h2 id="ipad-steps-title">{t("stepsTitle")}</h2>
          <p>{t("illustrativeNote")}</p>
        </div>
        <div className="site-ipad-steps">
          {steps.map(({ title, description, alt, Scene, ...step }, index) => (
            <figure className="site-ipad-step" key={title}>
              <div className="site-ipad-step-heading">
                <span className="site-ipad-step-number" aria-hidden="true">
                  {index + 1}
                </span>
                <h3>{t(title)}</h3>
              </div>
              <div
                className={`site-ipad-scene${"home" in step ? " site-ipad-home-scene" : ""}`}
                role="img"
                aria-label={t(alt)}
              >
                <div aria-hidden="true">
                  <Scene />
                </div>
              </div>
              <figcaption>
                <Trans t={t} i18nKey={description} />
              </figcaption>
            </figure>
          ))}
        </div>
      </section>
      <section className="site-section site-ipad-existing" aria-labelledby="ipad-existing-title">
        <h2 id="ipad-existing-title">{t("existingTitle")}</h2>
        <p>
          <Trans t={t} i18nKey="existingDescription" />
        </p>
        <p className="site-ipad-existing-extra">{t("existingExtra")}</p>
        {DRIVE_SYNC_RELEASED && (
          <p className="site-ipad-sync-note">
            {t("syncNote")} <a href="/sync">{t("syncLink")}</a>
          </p>
        )}
        <div className="site-ipad-sources">
          <a href="https://support.apple.com/zh-hk/guide/ipad/ipad8f1f7a29/ipados" target="_blank" rel="noreferrer">
            {t("appleLink")} <span aria-hidden="true">↗</span>
          </a>
          <a href="https://webkit.org/tracking-prevention/" target="_blank" rel="noreferrer">
            {t("webkitLink")} <span aria-hidden="true">↗</span>
          </a>
        </div>
      </section>
    </div>
  );
}
